<template>
    <Tooltip :is-disabled="!props.tooltipText">
        <template #trigger="tooltipProps">
            <span
                :aria-describedby="tooltipProps['aria-labelledby'] ? tooltipProps['aria-labelledby'] : undefined"
                :popovertarget="tooltipProps['popovertarget'] ? tooltipProps.popovertarget : undefined"
                class="inline-flex items-center rounded-md font-medium ring-1 ring-inset leading-none min-w-max"
                :class="[variantClasses, sizeClasses, $attrs.class]"
                :tabindex="props.tooltipText ? 0 : undefined"
            >
                <span v-if="hasIcon" class="flex items-center">
                    <slot name="icon" />
                </span>
                <slot />
            </span>
        </template>

        <template #text>
            <div class="w-max">{{ props.tooltipText }}</div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import Tooltip from '../Tooltip/Tooltip.vue';

export type BadgeVariant = 'Green' | 'Yellow' | 'Red' | 'Gray' | 'Purple' | 'Sky';

type BadgeProps = {
    variant: BadgeVariant;
    size: 'Extra-Small' | 'Small' | 'Base'; // Extra-Small = 24px height, Small = 32px height, Base = 40px height
    tooltipText?: string;
};

const props = defineProps<BadgeProps>();

const slots = useSlots();
const hasIcon = computed(() => Boolean(slots.icon));

const variantClasses = computed(() => {
    switch (props.variant) {
        case 'Green':
            return `
                bg-green-50 text-green-700 ring-green-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-700
            `;

        case 'Yellow':
            return `
                bg-yellow-50 text-yellow-700 ring-yellow-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-700
                `;

        case 'Red':
            return `
                bg-red-50 text-red-700 ring-red-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-700
            `;

        case 'Gray':
            return `
                bg-gray-50 text-gray-700 ring-gray-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-700
            `;

        case 'Purple':
            return `
                bg-purple-50 text-purple-700 ring-purple-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-700
            `;

        case 'Sky':
            return `
                bg-sky-50 text-sky-700 ring-sky-600/10
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-700
            `;

        default:
            throw new Error(`Unknown Badge variant: ${props.variant}`);
    }
});

const sizeClasses = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return hasIcon.value ? 'text-xs pl-1.5 pr-2 py-1.5 gap-1.25' : 'text-xs px-2 py-1.5';

        case 'Small':
            return hasIcon.value ? 'text-base pl-2 pr-2.5 py-2 gap-1.5' : 'text-base px-2.5 py-2';

        case 'Base':
            return hasIcon.value ? 'text-base pl-2.5 pr-3 py-3 gap-1.75' : 'text-base px-3 py-3';

        default:
            throw new Error(`Unknown Badge size: ${props.size}`);
    }
});
</script>
